/**
 * ChannelMixDonut — a channel-mix donut chart (Recharts) with a side legend.
 *
 * Renders each booking channel's share of confirmed revenue from the real
 * `/api/analytics/channel-mix` data. Colours come from a fixed brand-derived
 * palette (forest → sage → gold → slate → clay), capped at the guideline's
 * "never more than three data colours" spirit by using tints of the emerald
 * family plus the single gold accent.
 *
 * Client-only (imported by the console bundle, never SSR).
 */
import type { ReactElement } from "react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import type { ChannelMixRow } from "../services.js";

/** Brand-derived series palette (emerald family + one gold accent). */
const SERIES_COLORS = [
  "#1D35A9", // Primary blue
  "#12B76A", // Green
  "#0067B1", // Deep blue
  "#667085", // Slate gray
  "#F79009", // Amber
  "#84CAFF", // Light blue
];

interface MixDatum {
  readonly name: string;
  readonly value: number;
  readonly share: number;
  readonly revenueMinor: string;
}

function formatFull(minor: string): string {
  const major = Number(minor) / 100;
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(major);
  } catch {
    return `₹${major.toFixed(0)}`;
  }
}

function MixTooltip({
  active,
  payload,
}: {
  readonly active?: boolean;
  readonly payload?: readonly { readonly payload?: MixDatum }[];
}): ReactElement | null {
  if (active !== true || payload === undefined || payload.length === 0) {
    return null;
  }
  const d = payload[0]?.payload;
  if (d === undefined) {
    return null;
  }
  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip__title">{d.name}</p>
      <p className="chart-tooltip__row">
        {formatFull(d.revenueMinor)} · {(d.share * 100).toFixed(0)}%
      </p>
    </div>
  );
}

/** The channel-mix donut with an accessible legend list beside it. */
export function ChannelMixDonut({
  rows,
}: {
  readonly rows: readonly ChannelMixRow[];
}): ReactElement {
  const data: MixDatum[] = rows.map((r) => ({
    name: r.channel,
    value: Number(r.revenueMinor) / 100,
    share: r.revenueSharePct,
    revenueMinor: r.revenueMinor,
  }));

  if (data.length === 0) {
    return (
      <p className="console-empty">
        No channel activity yet — confirmed reservations will appear here.
      </p>
    );
  }

  return (
    <div className="chart-donut">
      <div className="chart-donut__chart" style={{ height: 200, width: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius={58}
              outerRadius={92}
              paddingAngle={1.5}
              stroke="#FFFFFF"
              strokeWidth={2}
            >
              {data.map((entry, i) => (
                <Cell
                  key={entry.name}
                  fill={SERIES_COLORS[i % SERIES_COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip content={<MixTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="chart-legend" aria-label="Channel mix legend">
        {data.map((d, i) => (
          <li key={d.name} className="chart-legend__item">
            <span
              className="chart-legend__swatch"
              style={{ background: SERIES_COLORS[i % SERIES_COLORS.length] }}
              aria-hidden="true"
            />
            <span className="chart-legend__label">{d.name}</span>
            <span className="chart-legend__value">
              {(d.share * 100).toFixed(0)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default ChannelMixDonut;
